import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Briefcase, Check, MonitorSmartphone, PhoneCall, Printer, Hotel } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useMeta } from "@/lib/useMeta";
import { lines, media } from "@/lib/api";
import { Rich, Reveal } from "@/components/site/Rich";
import { Carousel } from "@/components/site/Carousel";

const Btn = ({ to, children, variant = "solid", testid, className = "" }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-colors duration-200 ${variant === "solid" ? "bg-crimson text-white hover:bg-crimson-hover" : "border border-white/70 text-white hover:bg-white hover:text-charcoal"} ${className}`}
  >
    {children}
  </Link>
);

const panelBg = (h) => {
  const a = h.panel_opacity === undefined || h.panel_opacity === "" ? 0.6 : Number(h.panel_opacity);
  const m = /^#?([0-9a-f]{6})$/i.exec((h.panel_color || "").trim());
  const n = parseInt(m ? m[1] : "1F1C1B", 16);
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`;
};

const Hero = ({ h }) => (
  <section className="relative bg-charcoal overflow-hidden" data-testid="home-hero">
    <img src={media(h.bg_image)} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: h.bg_opacity === undefined || h.bg_opacity === "" ? 1 : Number(h.bg_opacity) }} />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 lg:py-24 grid lg:grid-cols-[1.35fr_1fr] gap-12 items-center">
      <Reveal className="min-w-0 text-white rounded-2xl border border-white/10 backdrop-blur-[3px] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)] px-6 py-8 sm:px-10 sm:py-10" style={{ background: panelBg(h) }}>
        <span className="inline-block bg-crimson text-white text-[11px] font-bold tracking-[0.25em] px-3 py-1.5 rounded-sm" data-testid="hero-label">{h.label}</span>
        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[2.5rem] xl:text-5xl leading-[1.08] break-words lg:whitespace-nowrap" data-testid="hero-title">{h.title}</h1>
        <p className="mt-5 text-base sm:text-lg text-stone-200 break-words" data-testid="hero-subtitle">{h.subtitle}</p>
        <ul className="mt-6 space-y-2.5">
          {lines(h.bullets_lines).map((b, i) => (
            <li key={i} className="flex gap-3 text-sm sm:text-[15px] text-stone-300 break-words" data-testid={`hero-bullet-${i}`}>
              <Check className="w-4 h-4 mt-0.5 shrink-0 text-brass" />
              <span className="min-w-0">{b}</span>
            </li>
          ))}
        </ul>
        <p className="mt-7 font-serif italic text-2xl sm:text-3xl text-brass" data-testid="hero-tagline">{h.tagline}</p>
        <span className="mt-5 inline-block border border-brass/60 text-brass text-xs px-4 py-1.5 rounded-full" data-testid="hero-pill">{h.pill}</span>
        <div className="mt-8 flex flex-wrap gap-3">
          <Btn to="/digital-edition" testid="hero-digital-edition-btn"><BookOpen className="w-4 h-4" />{h.button_primary}</Btn>
          <Btn to="/listing-options" variant="outline" testid="hero-list-hotel-btn">{h.button_secondary}<ArrowRight className="w-4 h-4" /></Btn>
        </div>
      </Reveal>
      <Reveal delay={0.15} className="flex justify-center lg:justify-end">
        <Link to="/digital-edition" className="relative group block w-[220px] sm:w-[300px] lg:w-[380px]" data-testid="hero-cover-link">
          <img src={media(h.cover_image)} alt="IHD 2026 cover" className="w-full h-auto shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7)] group-hover:-translate-y-1 transition-transform duration-300" />
          <span className="absolute bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white text-crimson text-xs font-bold px-4 py-2 rounded-full shadow-lg flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" />{h.cover_button}
          </span>
        </Link>
      </Reveal>
    </div>
  </section>
);

const TextLink = ({ to, children, testid }) => (
  <Link to={to} data-testid={testid} className="inline-flex items-center gap-1.5 text-sm font-bold text-crimson hover:text-crimson-hover group">
    {children}<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
  </Link>
);

const Split = ({ s, reverse, testid, link, children }) => (
  <section className={`${reverse ? "bg-white" : "bg-ivory"} py-20 sm:py-28`} data-testid={testid}>
    <div className={`max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center`}>
      <Reveal className={`min-w-0 ${reverse ? "lg:order-2" : ""}`}>
        <span className="block h-[2px] w-12 bg-brass mb-5" />
        <h2 className="text-3xl sm:text-4xl text-charcoal">{s.title}</h2>
        <Rich html={s.body_html} className="mt-5 text-stone-600 leading-relaxed text-sm sm:text-base" />
        {children}
        {link && <div className="mt-6">{link}</div>}
      </Reveal>
      <Reveal delay={0.1} className={reverse ? "lg:order-1" : ""}>
        <img src={media(s.image)} alt="" className="w-full aspect-[4/3] object-cover rounded-md shadow-xl" />
      </Reveal>
    </div>
  </section>
);

const PromoCard = ({ c, to, testid, delay }) => (
  <Reveal delay={delay} className="h-full">
    <div className="bg-white h-full flex flex-col rounded-md overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-300 border border-stone-100" data-testid={testid}>
      <img src={media(c.image)} alt="" className="w-full h-40 object-cover" />
      <div className="p-7 flex flex-col flex-1">
        <span className="block h-[2px] w-10 bg-brass mb-4" />
        <h3 className="text-2xl text-crimson mb-4">{c.title}</h3>
        <Rich html={c.body_html} className="text-sm text-stone-600 leading-relaxed flex-1" />
        <div className="mt-6"><TextLink to={to} testid={`${testid}-link`}>{c.link_text}</TextLink></div>
      </div>
    </div>
  </Reveal>
);

const Features = ({ f }) => {
  const icons = [Briefcase, PhoneCall, MonitorSmartphone];
  return (
    <section className="bg-charcoal py-20 sm:py-28" data-testid="home-features">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <span className="block h-[2px] w-12 bg-brass mb-5" />
        <h2 className="text-3xl sm:text-4xl text-white max-w-2xl">{f.title}</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[1, 2, 3].map((n, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={n} delay={i * 0.1}>
                <div className="h-full bg-white/[0.04] border border-white/10 rounded-md p-7 hover:border-brass/50 transition-colors" data-testid={`feature-card-${n}`}>
                  <div className="w-11 h-11 rounded-md bg-crimson flex items-center justify-center mb-6"><Icon className="w-5 h-5 text-white" /></div>
                  <h3 className="text-xl text-white mb-3">{f[`f${n}_title`]}</h3>
                  <p className="text-sm text-stone-400 leading-relaxed">{f[`f${n}_body`]}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-12"><Btn to="/listing-options" testid="features-list-hotel-btn">{f.button_text}<ArrowRight className="w-4 h-4" /></Btn></div>
      </div>
    </section>
  );
};

const CarouselBand = ({ s, bg, testid }) => (
  <section className={`${bg} py-16 sm:py-24`} data-testid={testid}>
    <h2 className="text-2xl sm:text-3xl lg:text-4xl text-charcoal text-center mb-12 px-4">{s.title}</h2>
    <Carousel images={s.images} testid={`${testid}-carousel`} />
  </section>
);

export default function Home() {
  const { content: c } = useSite();
  useMeta("home");
  const pills = [[Hotel, c.device.pill1], [Printer, c.device.pill2], [MonitorSmartphone, c.device.pill3]];
  return (
    <div data-testid="home-page">
      <Hero h={c.hero} />
      <Split s={c.fingertips} testid="home-fingertips" link={<TextLink to="/digital-edition" testid="fingertips-link">{c.fingertips.link_text}</TextLink>} />
      <section className="bg-ivory-alt py-20 sm:py-28" data-testid="home-cards">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-3 gap-6 lg:gap-8">
          <PromoCard c={c.card1} to="/digital-edition" testid="promo-card-1" delay={0} />
          <PromoCard c={c.card2} to="/contact" testid="promo-card-2" delay={0.1} />
          <PromoCard c={c.card3} to="/listing-options" testid="promo-card-3" delay={0.2} />
        </div>
      </section>
      <Split s={c.device} reverse testid="home-device">
        <div className="mt-6 flex flex-wrap gap-2">
          {pills.map(([Icon, label], i) => (
            <span key={i} className="inline-flex items-center gap-1.5 text-xs text-crimson border border-crimson/30 bg-crimson/5 rounded-full px-3 py-1.5" data-testid={`device-pill-${i}`}><Icon className="w-3.5 h-3.5" />{label}</span>
          ))}
        </div>
      </Split>
      <Features f={c.features} />
      <section className="bg-white py-16 sm:py-20 border-b border-stone-200" data-testid="home-statement">
        <Reveal className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Rich html={c.statement.text_html} className="font-bold text-lg sm:text-xl lg:text-2xl leading-relaxed text-charcoal" />
        </Reveal>
      </section>
      <CarouselBand s={c.members} bg="bg-[#ECE9E4]" testid="home-members" />
      <CarouselBand s={c.editions} bg="bg-ivory" testid="home-editions" />
    </div>
  );
}
